import { useState, useMemo } from 'react'

export default function GenerationRulesPanel({
  account,
  generationRule,
  ruleForm,
  setRuleForm,
  rulesPanelOpen,
  setRulesPanelOpen,
  onSave,
  onExecute,
  onResetStatus,
  ruleSaving,
  minStartDate
}) {
  const [previewSchedule, setPreviewSchedule] = useState(null)

  const previewMap = useMemo(() => {
    if (!previewSchedule) return {}
    const map = {}
    if (ruleForm.generationMode === 'AUTO') {
      // AUTO 모드: 오늘부터 daysAhead일 후까지 미리보기
      const daysAhead = ruleForm.daysAhead || 3
      for (let i = 0; i <= daysAhead; i++) {
        const d = new Date()
        d.setDate(d.getDate() + i)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        map[key] = ruleForm.dailyCount
      }
    } else if (ruleForm.generationMode === 'DAILY') {
      let d = new Date(ruleForm.startDate)
      const end = new Date(ruleForm.endDate)
      while (d <= end) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        map[key] = ruleForm.dailyCount
        d.setDate(d.getDate() + 1)
      }
    } else {
      const d = new Date(ruleForm.startDate)
      const end = new Date(ruleForm.endDate)
      let totalDays = 0
      const temp = new Date(d)
      while (temp <= end) { totalDays++; temp.setDate(temp.getDate() + 1) }
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      map[key] = totalDays * ruleForm.dailyCount
    }
    return map
  }, [previewSchedule, ruleForm])

  const ruleSummary = () => {
    if (!generationRule) return '규칙 미설정'
    if (generationRule.generationMode === 'AUTO') {
      const status = generationRule.autoGenerate ? 'ON' : 'OFF'
      return `자동 ${generationRule.dailyCount}개/일, ${generationRule.daysAhead || 3}일 앞서 생성 [${status}]`
    }
    const mode = generationRule.generationMode === 'DAILY' ? '매일 분배' : '한번에 생성'
    return `${mode} ${generationRule.dailyCount}개, ${generationRule.startDate} ~ ${generationRule.endDate}`
  }

  const handleSave = async () => {
    await onSave()
    setPreviewSchedule(null)
  }

  return {
    previewMap,
    previewSchedule,
    render: (
      <div className="mb-6 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <button
          onClick={() => setRulesPanelOpen(!rulesPanelOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-lg">tune</span>
            <span className="font-semibold text-sm dark:text-white">생성 규칙</span>
            {generationRule?.status && (
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                generationRule.status === 'RUNNING' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                generationRule.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                generationRule.status === 'FAILED' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                generationRule.status === 'PAUSED' ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' :
                'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              }`}>
                {generationRule.status === 'RUNNING' ? '실행 중' :
                 generationRule.status === 'COMPLETED' ? '완료' :
                 generationRule.status === 'FAILED' ? '실패' :
                 generationRule.status === 'PAUSED' ? '일시정지' : '대기'}
              </span>
            )}
            <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">{ruleSummary()}</span>
          </div>
          <span className={`material-symbols-outlined text-slate-400 transition-transform ${rulesPanelOpen ? 'rotate-180' : ''}`}>
            expand_more
          </span>
        </button>

        {rulesPanelOpen && (
          <div className="p-5 space-y-5 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
            {/* 생성 방식 */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">생성 방식</label>
              <div className="flex gap-3">
                {[
                  { value: 'AUTO', label: '자동 생성', desc: '매일 새벽 자동으로 생성', icon: 'smart_toy' },
                  { value: 'DAILY', label: '수동 분배', desc: '기간 내 매일 N개씩 생성', icon: 'calendar_today' },
                  { value: 'BATCH', label: '수동 일괄', desc: '전체 수량을 즉시 생성', icon: 'flash_on' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setRuleForm(prev => ({ ...prev, generationMode: opt.value }))}
                    className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${
                      ruleForm.generationMode === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`material-symbols-outlined text-sm ${ruleForm.generationMode === opt.value ? 'text-primary' : 'text-slate-400'}`}>
                        {opt.icon}
                      </span>
                      <span className={`text-sm font-bold ${ruleForm.generationMode === opt.value ? 'text-primary' : 'dark:text-white'}`}>
                        {opt.label}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* 일별 수량 + 기간/자동설정 */}
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">일별 이미지 수</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setRuleForm(prev => ({ ...prev, dailyCount: Math.max(1, prev.dailyCount - 1) }))}
                    className="size-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm dark:text-white">remove</span>
                  </button>
                  <span className="text-2xl font-bold dark:text-white w-8 text-center">{ruleForm.dailyCount}</span>
                  <button
                    onClick={() => setRuleForm(prev => ({ ...prev, dailyCount: Math.min(10, prev.dailyCount + 1) }))}
                    className="size-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm dark:text-white">add</span>
                  </button>
                  <span className="text-xs text-slate-400">/ 일</span>
                </div>
                {ruleForm.generationMode === 'BATCH' && ruleForm.startDate && ruleForm.endDate && (
                  <p className="text-[11px] text-primary mt-1.5">
                    총 {(() => {
                      const s = new Date(ruleForm.startDate), e = new Date(ruleForm.endDate)
                      const days = Math.floor((e - s) / 86400000) + 1
                      return days * ruleForm.dailyCount
                    })()}개 생성 예정
                  </p>
                )}
              </div>

              {ruleForm.generationMode === 'AUTO' ? (
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">미리 생성 일수</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setRuleForm(prev => ({ ...prev, daysAhead: Math.max(1, (prev.daysAhead || 3) - 1) }))}
                      className="size-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm dark:text-white">remove</span>
                    </button>
                    <span className="text-2xl font-bold dark:text-white w-8 text-center">{ruleForm.daysAhead || 3}</span>
                    <button
                      onClick={() => setRuleForm(prev => ({ ...prev, daysAhead: Math.min(14, (prev.daysAhead || 3) + 1) }))}
                      className="size-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm dark:text-white">add</span>
                    </button>
                    <span className="text-xs text-slate-400">일 앞서</span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                    매일 새벽 자동으로 {ruleForm.daysAhead || 3}일 후까지 게시물이 없는 날을 채웁니다
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">생성 기간</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={ruleForm.startDate || ''}
                      min={minStartDate}
                      onChange={(e) => {
                        const newStart = e.target.value
                        setRuleForm(prev => ({
                          ...prev,
                          startDate: newStart,
                          endDate: prev.endDate && prev.endDate < newStart ? newStart : prev.endDate
                        }))
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white"
                    />
                    <span className="text-slate-400 text-xs">~</span>
                    <input
                      type="date"
                      value={ruleForm.endDate || ''}
                      min={ruleForm.startDate || minStartDate}
                      onChange={(e) => setRuleForm(prev => ({ ...prev, endDate: e.target.value }))}
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* AUTO 모드 ON/OFF 토글 */}
            {ruleForm.generationMode === 'AUTO' && (
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <div>
                  <span className="text-sm font-bold dark:text-white">자동 생성</span>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">ON이면 매일 새벽 자동으로 콘텐츠를 생성합니다</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={ruleForm.autoGenerate}
                  onClick={() => setRuleForm(prev => ({ ...prev, autoGenerate: !prev.autoGenerate }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    ruleForm.autoGenerate ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    ruleForm.autoGenerate ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            )}

            {/* 버튼 */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setPreviewSchedule(Date.now())}
                className="px-4 py-2 text-sm font-semibold text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
              >
                <span className="material-symbols-outlined text-sm align-middle mr-1">visibility</span>
                미리보기
              </button>
              <button
                onClick={handleSave}
                disabled={ruleSaving}
                className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {ruleSaving ? '저장 중...' : '저장'}
              </button>
              {generationRule && generationRule.status !== 'RUNNING' && ruleForm.generationMode !== 'AUTO' && (
                <button
                  onClick={onExecute}
                  className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:opacity-90 transition-opacity"
                >
                  <span className="material-symbols-outlined text-sm align-middle mr-1">play_arrow</span>
                  생성 시작
                </button>
              )}
              {generationRule && generationRule.status && generationRule.status !== 'READY' && (
                <button
                  onClick={onResetStatus}
                  className="px-4 py-2 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/40"
                >
                  <span className="material-symbols-outlined text-sm align-middle mr-1">restart_alt</span>
                  상태 초기화
                </button>
              )}
              {previewSchedule && (
                <button
                  onClick={() => setPreviewSchedule(null)}
                  className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  미리보기 해제
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
}
